import React, { useEffect, useState } from "react";
import inverterService from "../services/inverterService";
import "./InverterPanel.css";

const POLL_INTERVAL_MS = 3000;

const fmt = (value, decimals = 1) => {
  if (value === undefined || value === null) return "--";
  return Number(value).toFixed(decimals);
};

const SunIcon = () => (
  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
);
const BatteryIcon = () => (
  <svg viewBox="0 0 24 24"><rect x="2" y="7" width="18" height="10" rx="2"/><path d="M22 11v2"/></svg>
);
const GridIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M2 12h20M12 2v20M2 7h20M2 17h20M7 2v20M17 2v20"/></svg>
);
const LoadIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M3 12a9 9 0 0 1 18 0M12 12v6"/><circle cx="12" cy="12" r="2"/></svg>
);
const WaveIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M2 12c2-4 4-4 6 0s4 4 6 0 4-4 6 0"/></svg>
);
const StateIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
);
const ChevronIcon = ({ down }) => (
  <svg
    viewBox="0 0 24 24"
    style={{ transform: down ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }}
  >
    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InverterPanel = ({ onStatusChange }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const result = await inverterService.getInverterData();
        if (!cancelled) { setData(result); setError(""); setLoading(false); }
      } catch (err) {
        if (!cancelled) { setError(err.message); setLoading(false); }
      }
    };
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    const timestamp = data?.timestamp ? new Date(data.timestamp).getTime() : 0;
    onStatusChange?.(error || data?.error || !timestamp || Date.now() - timestamp > 30000 ? 'unknown' : data?.active ? 'online' : 'offline');
  }, [data, error, onStatusChange]);

  if (loading) {
    return (
      <div className="inv-panel inv-loading">
        <span className="inv-spinner" />
        Loading inverter...
      </div>
    );
  }

  if (error && !data) {
    return <div className="inv-panel inv-error-msg">{error}</div>;
  }

  const ts = data?.timestamp ? new Date(data.timestamp).toLocaleTimeString() : null;
  const online = data?.active;

  return (
    <div className="inv-panel">
      <div
        className="inv-header"
        onClick={() => setCollapsed((c) => !c)}
        title="Tap to expand/collapse"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setCollapsed((c) => !c); }}
      >
        <span className="inv-dot" data-active={String(!!online)} />
        <svg className="inv-title-icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
        <strong className="inv-title">Inverter</strong>
        <span className="inv-chevron" aria-hidden="true">
          <ChevronIcon down={!collapsed} />
        </span>
        <span className={`inv-badge ${online ? "online" : "offline"}`}>
          {online ? "Online" : "Offline"}
        </span>
      </div>
      {!collapsed && ts && <div className="inv-timestamp">Updated {ts}</div>}

      {collapsed ? (
        <div className="inv-summary-row">
          <div className="inv-hero inv-hero-pv">
            <small>PV Power</small>
            <strong>{fmt(data?.pv_power)} <span>W</span></strong>
            <span className="inv-hero-icon"><SunIcon /></span>
          </div>
          <div className="inv-hero inv-hero-batt">
            <small>Battery SOC</small>
            <strong>{data?.batt_soc ?? "--"}%</strong>
            <span className="inv-hero-icon"><BatteryIcon /></span>
          </div>
          <div className="inv-hero inv-hero-load">
            <small>Load Power</small>
            <strong>{fmt(data?.load_power)} <span>W</span></strong>
            <span className="inv-hero-icon"><LoadIcon /></span>
          </div>
        </div>
      ) : (
        <>
          <div className="inv-hero-row">
            <div className="inv-hero inv-hero-pv">
              <small>PV Power</small>
              <strong>{fmt(data?.pv_power)} <span>W</span></strong>
              <span className="inv-hero-icon"><SunIcon /></span>
            </div>
            <div className="inv-hero inv-hero-volt">
              <small>PV Voltage</small>
              <strong>{fmt(data?.pv_voltage)} <span>V</span></strong>
              <span className="inv-hero-icon"><GridIcon /></span>
            </div>
            <div className="inv-hero inv-hero-current">
              <small>PV Current</small>
              <strong>{fmt(data?.pv_current, 2)} <span>A</span></strong>
              <span className="inv-hero-icon"><WaveIcon /></span>
            </div>
          </div>

          <div className="inv-grid">
            <div className="inv-metric"><span className="inv-metric-icon batt"><BatteryIcon /></span><small>Battery SOC</small><strong>{data?.batt_soc ?? "--"}%</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon batt"><BatteryIcon /></span><small>Battery Voltage</small><strong>{fmt(data?.batt_voltage, 2)} V</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon batt"><BatteryIcon /></span><small>Battery Current</small><strong>{fmt(data?.batt_current, 2)} A</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon batt"><BatteryIcon /></span><small>Battery Power</small><strong>{fmt(data?.batt_power)} W</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon grid"><GridIcon /></span><small>Grid Voltage</small><strong>{fmt(data?.grid_voltage)} V</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon grid"><GridIcon /></span><small>Grid Current</small><strong>{fmt(data?.grid_current, 2)} A</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon grid"><GridIcon /></span><small>Grid Power</small><strong>{fmt(data?.grid_power)} W</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon load"><LoadIcon /></span><small>Load Power</small><strong>{fmt(data?.load_power)} W</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon freq"><WaveIcon /></span><small>Grid Freq</small><strong>{fmt(data?.grid_freq, 2)} Hz</strong></div>
            <div className="inv-metric"><span className="inv-metric-icon state"><StateIcon /></span><small>State</small><strong>{data?.state ?? "--"}</strong></div>
          </div>
        </>
      )}

      <div className="desktop-inverter-extra">
        <div className="desktop-battery-track"><div style={{ width: `${Math.max(0, Math.min(100, Number(data?.batt_soc) || 0))}%` }} /></div>
        {Number.isFinite(data?.load_power) && Number.isFinite(data?.pv_power) && <p>{data.load_power >= data.pv_power ? `Load exceeds solar by ${fmt(data.load_power - data.pv_power, 0)} W` : `Solar exceeds load by ${fmt(data.pv_power - data.load_power, 0)} W`}</p>}
        <footer><small>Updated {ts || '—'}</small><button onClick={() => setCollapsed((c) => !c)}>{collapsed ? 'All metrics' : 'Summary'}</button></footer>
      </div>
      {data?.error && <div className="inv-error-msg">Inverter: {data.error}</div>}
      {error && <div className="inv-error-msg">{error}</div>}
    </div>
  );
};

export default InverterPanel;
